import {isEmpty} from 'lodash';
import React, {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import {useDebounceState} from '../../util/hooks';
import {searchRegexFromQuery} from '../../util/panelbank';

const JSON_TREE_SEARCH_DEBOUNCE_MS = 100;

const JSONTreeSearchInputContext = createContext<{
  searchQuery: string;
  onChangeSearchQuery(newSearchQuery: string): void;
}>({
  searchQuery: '',
  onChangeSearchQuery: () => {},
});
JSONTreeSearchInputContext.displayName = 'JSONTreeSearchInputContext';

const JSONTreeSearchDebouncedContext = createContext<{
  isSearching: boolean;
  getIsSearchMatch: (test: string) => boolean;
}>({
  isSearching: false,
  getIsSearchMatch: () => false,
});
JSONTreeSearchDebouncedContext.displayName = 'JSONTreeSearchDebouncedContext';

type JSONTreeSearchContextProviderProps = {
  children: ReactNode;
};

const JSONTreeSearchContextProviderComp: React.FC<
  JSONTreeSearchContextProviderProps
> = ({children}) => {
  const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebounceState(
    '',
    JSON_TREE_SEARCH_DEBOUNCE_MS
  );

  const onChangeSearchQuery = useCallback(
    (newSearchQuery: string) => {
      setSearchQuery(newSearchQuery);
    },
    [setSearchQuery]
  );

  const inputState = useMemo(
    () => ({
      searchQuery,
      onChangeSearchQuery,
    }),
    [searchQuery, onChangeSearchQuery]
  );

  const trimmedDebouncedSearchQuery = debouncedSearchQuery.trim();
  const isSearching = !isEmpty(trimmedDebouncedSearchQuery);
  const debouncedSearchRegex = useMemo(
    () => searchRegexFromQuery(trimmedDebouncedSearchQuery),
    [trimmedDebouncedSearchQuery]
  );
  const getIsSearchMatch = useCallback(
    (testString: string) => {
      if (debouncedSearchRegex == null) {
        return true;
      }
      return debouncedSearchRegex.test(testString);
    },
    [debouncedSearchRegex]
  );

  const debouncedState = useMemo(
    () => ({
      isSearching,
      getIsSearchMatch,
    }),
    [getIsSearchMatch, isSearching]
  );

  return (
    <JSONTreeSearchInputContext.Provider value={inputState}>
      <JSONTreeSearchDebouncedContext.Provider value={debouncedState}>
        {children}
      </JSONTreeSearchDebouncedContext.Provider>
    </JSONTreeSearchInputContext.Provider>
  );
};

export const JSONTreeSearchContextProvider = memo(
  JSONTreeSearchContextProviderComp
);

export function useJSONTreeSearchInputContext() {
  return useContext(JSONTreeSearchInputContext);
}

export function useJSONTreeSearchDebouncedContext() {
  return useContext(JSONTreeSearchDebouncedContext);
}
